import type { SalesOrgLeadStatus } from "@xop/dashboard-shared";

/**
 * Lead pipeline-stage helpers — kept out of the LeadPage component so the pure
 * status → label / status → operator-moves logic is unit-testable, and so the
 * `LifecycleBar` comparisons that drive the moves are pinned by the shared
 * `SalesOrgLeadStatus` union (not loose string literals). Mirrors the agent's
 * `OrgLeadStatus` enum — see `packages/shared/src/sales.ts`.
 */

/** Every pipeline stage the agent persists + emits, in pipeline order. */
export const LEAD_STATUSES: readonly SalesOrgLeadStatus[] = [
  "new",
  "qualified",
  "contacted",
  "denied",
  "dismissed",
  "exported",
];

/**
 * Human label for a pipeline status. Takes a plain `string` (not the union) on
 * purpose: the API hands us raw strings, so an unfamiliar future stage falls
 * through to itself rather than blanking the pill.
 *
 * The operator only ever sees THREE buckets (2026-06-16): **New**, **Qualified**
 * (the agent-set `qualified` stage AND the CRM-pushed `exported` stage both read
 * "qualified" — a lead good enough to work / qualify into the CRM), and
 * **Rejected** (`denied` = operator-declined AND `dismissed` = agent-archived
 * both read "rejected" — no further action). The legacy `contacted` stage is
 * retired — the agent never sets it (it is search-only) and the operator can no
 * longer move a lead there — but a historical `contacted` row still renders
 * gracefully rather than blanking.
 */
export function statusLabel(s: string): string {
  switch (s) {
    case "new":        return "new";
    case "qualified":  return "✓ qualified";
    // The CRM-pushed stage reads as "qualified" to the operator — a lead they
    // (or the agent) deemed good enough to qualify into the CRM as a prospect.
    case "exported":   return "✓ qualified";
    case "denied":     return "✕ rejected";
    // Agent-archived junk also reads "rejected" — both mean "no further action".
    case "dismissed":  return "✕ rejected";
    // Legacy/retired stage — kept labelled so historical rows aren't blanked.
    case "contacted":  return "contacted";
    default:           return s;
  }
}

/** One operator pipeline move offered on the LeadPage lifecycle bar. */
export interface LifecycleAction {
  label: string;
  status: SalesOrgLeadStatus;
  /** Destructive move — rendered in the error tone. */
  danger?: boolean;
}

/**
 * The sensible operator moves for a lead at the given pipeline stage, in the
 * 3-bucket operator model (2026-06-16): **New** / **Qualified** / **Rejected**.
 *
 * - **Reject** sets the canonical `denied` stage ("no further action"). Offered
 *   unless the lead is already rejected (`denied`/`dismissed`).
 * - **Back to New** re-opens any non-`new` lead.
 *
 * Qualifying is NOT here — "Qualify for CRM" (which sets `exported`) is a
 * distinct action with its own button/menu entry, mirroring the agent's
 * `_SETTABLE_STATUSES` allowlist (`exported` is set only by the CRM-export
 * flow, never a plain status move). The retired `contacted` and `dismissed`
 * (Archive) moves are gone — the operator no longer drives a lead into them.
 */
export function lifecycleActions(status: SalesOrgLeadStatus): LifecycleAction[] {
  const actions: LifecycleAction[] = [];
  if (status !== "denied" && status !== "dismissed")
    actions.push({ label: "Reject", status: "denied", danger: true });
  if (status !== "new") actions.push({ label: "Back to New", status: "new" });
  return actions;
}
